import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { bodyTooltip, chartTitle, type OptionContext } from './shared.ts';

type BubblesConfig = Extract<ChartConfig, { type: 'bubbles' }>;

type PlacedCircle = { x: number; y: number; r: number };

function stanceFill(
  stance: 'praise' | 'friction' | undefined,
  dark: boolean
): string {
  // Teal praise / romance-red friction (mid fills, white labels).
  if (stance === 'friction') return dark ? '#8b4554' : '#7a3d4a';
  return dark ? '#3d8f8a' : '#347a75';
}

function symbolSizeFor(value: number, minV: number, maxV: number): number {
  if (maxV <= minV) return 80;
  const t = (value - minV) / (maxV - minV);
  // Sized so longer emotion labels stay inside the bubble
  return Math.round(56 + t * 60);
}

/** Deterministic circle pack — largest at origin, others nestle around it. */
export function packCircles(radii: number[]): PlacedCircle[] {
  const placed: PlacedCircle[] = [];
  const angleSteps = 72;
  const gap = 12;

  for (const r of radii) {
    if (placed.length === 0) {
      placed.push({ x: 0, y: 0, r });
      continue;
    }

    let best: PlacedCircle | null = null;
    let bestDist = Infinity;

    for (const p of placed) {
      for (let a = 0; a < angleSteps; a++) {
        const angle = (a / angleSteps) * Math.PI * 2;
        const x = p.x + (p.r + r + gap) * Math.cos(angle);
        const y = p.y + (p.r + r + gap) * Math.sin(angle);
        const fits = placed.every((q) => {
          const dx = x - q.x;
          const dy = y - q.y;
          return Math.hypot(dx, dy) + 0.01 >= q.r + r + gap;
        });
        if (!fits) continue;
        const dist = Math.hypot(x, y);
        if (dist < bestDist) {
          bestDist = dist;
          best = { x, y, r };
        }
      }
    }

    placed.push(best ?? { x: placed.length * (r + gap) * 2, y: 0, r });
  }

  return placed;
}

/** Map pack coords into the unit plane with padding (stable across remounts). */
export function toUnitPositions(
  circles: PlacedCircle[]
): Array<{ x: number; y: number }> {
  if (circles.length === 0) return [];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const c of circles) {
    minX = Math.min(minX, c.x - c.r);
    maxX = Math.max(maxX, c.x + c.r);
    minY = Math.min(minY, c.y - c.r);
    maxY = Math.max(maxY, c.y + c.r);
  }

  const midX = (minX + maxX) / 2;
  // Stretch horizontally so the pack reads wider than tall.
  const stretchX = 1.7;
  const stretched = circles.map((c) => ({
    ...c,
    x: midX + (c.x - midX) * stretchX,
  }));

  minX = Infinity;
  maxX = -Infinity;
  for (const c of stretched) {
    minX = Math.min(minX, c.x - c.r);
    maxX = Math.max(maxX, c.x + c.r);
  }

  const w = Math.max(maxX - minX, 1);
  const h = Math.max(maxY - minY, 1);
  const padX = 0.04;
  const padY = 0.12;
  const spanX = 1 - 2 * padX;
  const spanY = 1 - 2 * padY;

  return stretched.map((c) => ({
    x: ((c.x - minX) / w) * spanX + padX,
    y: ((c.y - minY) / h) * spanY + padY,
  }));
}

/**
 * Packed emotion bubbles — size = prevalence, no network links.
 * Static pack so positions stay put across remounts/resizes.
 */
export function buildBubblesOption(
  config: BubblesConfig,
  { theme, base }: OptionContext
): EChartsOption {
  const { textColor, dark, axisLineColor } = theme;
  const pts = config.points;
  const values = pts.map((p) => p.value);
  const minV = values.length ? Math.min(...values) : 0;
  const maxV = values.length ? Math.max(...values) : 1;
  const onBubbleLabel = 'rgba(255, 255, 255, 0.95)';
  // Dark stroke keeps overflow readable on light page chrome
  const labelStroke = dark
    ? 'rgba(15, 15, 20, 0.35)'
    : 'rgba(40, 40, 48, 0.55)';

  // Largest first so the pack anchors on the dominant emotion.
  const ordered = pts
    .map((p, index) => ({ p, index, size: symbolSizeFor(p.value, minV, maxV) }))
    .sort((a, b) => b.size - a.size || a.index - b.index);

  const radii = ordered.map((o) => o.size / 2);
  const packed = packCircles(radii);
  const positions = toUnitPositions(packed);

  const data = ordered.map((o, i) => {
    const pos = positions[i] ?? { x: 0.5, y: 0.5 };
    return {
      name: o.p.name,
      value: [pos.x, pos.y, o.p.value],
      symbolSize: o.size,
      itemStyle: {
        color: stanceFill(o.p.stance, dark),
        borderColor: dark
          ? 'rgba(15, 15, 20, 0.85)'
          : 'rgba(255, 255, 255, 0.9)',
        borderWidth: 2,
      },
      label: {
        show: o.size >= 52,
        color: onBubbleLabel,
        fontSize: o.size >= 72 ? 12 : 10,
        formatter: o.p.name,
        textBorderColor: labelStroke,
        textBorderWidth: dark ? 1 : 2,
      },
    };
  });

  return {
    ...base,
    title: chartTitle(config.title, textColor, 'center'),
    tooltip: bodyTooltip({
      trigger: 'item',
      formatter: (p) => {
        const item = p as {
          name?: string;
          data?: { value?: number[]; name?: string };
        };
        const name = item.data?.name ?? item.name ?? 'Emotion';
        const pct = item.data?.value?.[2];
        return typeof pct === 'number' ? `${name}: ${pct}%` : name;
      },
    }),
    grid: {
      left: 4,
      right: 4,
      top: config.title ? 28 : 4,
      bottom: 4,
      containLabel: false,
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 1,
      show: false,
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 1,
      show: false,
      splitLine: { show: false },
      inverse: true,
    },
    series: [
      {
        type: 'scatter' as const,
        data,
        animation: false,
        label: {
          position: 'inside' as const,
        },
        emphasis: {
          scale: false,
          label: { show: true, color: onBubbleLabel },
          itemStyle: {
            borderColor: axisLineColor,
          },
        },
      },
    ],
  };
}
